<mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedIndexChange)="onTabChange($event)"
  class="flex flex-col min-h-0 !max-h-100%"
  [ngClass]="visitFlow ? 'patient-selector-framed-tabs' : 'border border-surface-container-highest rounded-lg'">
  <mat-tab [label]="'Scheduled patients' | translate">
    <div class="pt-4 pb-2 flex flex-col" [ngClass]="visitFlow ? 'px-0' : 'px-2'">
      <mat-form-field appearance="outline" class="w-full mb-3 [&_.mat-mdc-form-field-icon-suffix]:!translate-x-[-10px]" subscriptSizing="dynamic">
        <mat-label>{{ (isLegalOrg ? 'Search patient (by name or ID)' : 'Search patient (by name or ID or DOB)') | translate }}</mat-label>
        <input matInput [formControl]="searchQuery" (input)="onSearchQueryCleared()" (keydown.enter)="filterSubjects()" />
        <button mat-icon-button matSuffix class="!bg-white" aria-label="Search" (click)="filterSubjects()">
          <mat-icon>search</mat-icon>
        </button>
      </mat-form-field>

      @if (visitFlow) {
        <div class="flex items-center gap-3 mb-4">
          <button type="button" class="patient-selector-chip" [matMenuTriggerFor]="dateMenu" #dateTrigger="matMenuTrigger"
            data-testid="patient-selector-date-chip">
            <span>{{ dateChipLabel | translate }}</span>
            <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">keyboard_arrow_down</mat-icon>
          </button>
          <mat-menu #dateMenu class="patient-selector-date-menu">
            <!-- Keeps month navigation from closing the menu or being read as menu navigation; Escape still closes. -->
            <div (click)="$event.stopPropagation()" (keydown)="onCalendarKeydown($event)">
              <mat-calendar [startAt]="selectedDate" [selected]="selectedDate"
                (selectedChange)="onScheduleDateChange($event); dateTrigger.closeMenu()"></mat-calendar>
            </div>
          </mat-menu>

          <button type="button" class="patient-selector-chip" [matMenuTriggerFor]="patientListMenu"
            data-testid="patient-selector-list-chip">
            <span>{{ listChipLabel | translate }}</span>
            <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">keyboard_arrow_down</mat-icon>
          </button>
          <mat-menu #patientListMenu>
            <button mat-menu-item (click)="onPatientListChange('all')">{{ 'All lists' | translate }}</button>
            <button mat-menu-item *ngFor="let g of patientListGroups; trackBy: trackByGroupCode"
              (click)="onPatientListChange(g.code)">{{ g.code }} ({{ g.count }})</button>
          </mat-menu>
        </div>
      } @else {
        <div class="flex items-center gap-2 mb-4">
          <app-dmy-date-field
            [value]="selectedDate"
            (valueChange)="onScheduleDateChange($event)"
            mode="calendar"
            [allowEmpty]="false"
            [label]="'Date' | translate"
          />

          <mat-form-field appearance="outline" class="min-w-[200px]" subscriptSizing="dynamic">
            <mat-label>{{ 'Patient list' | translate }}</mat-label>
            <mat-select [(ngModel)]="selectedListCode" (ngModelChange)="onPatientListChange($event)">
              <mat-option value="all">{{ 'All lists' | translate }}</mat-option>
              <mat-option *ngFor="let g of patientListGroups; trackBy: trackByGroupCode" [value]="g.code">
                {{ g.code }} ({{ g.count }})
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      }

      <div class="label-medium text-outline mb-2">{{ 'Your scheduled patients' | translate }}</div>
      <div #scheduledPatientsList class="relative overflow-y-auto grow"
        [ngClass]="visitFlow ? 'h-[298px]' : 'h-[196px]'">
        @if (showLoadingSkeleton) {
          <app-appointment-skeleton variant="selector" [count]="2"></app-appointment-skeleton>
        }
        @else if (filteredSubjects.value.length > 0) {
          <ul class="space-y-2 grow">
            <li *ngFor="let item of filteredSubjects.value"
              class="flex p-4 rounded-lg border border-surface-container-highest hover:bg-surface-container-high cursor-pointer"
              [ngClass]="rowTint(item)"
              (click)="onRowClick(item, 'schedule')"
              data-testid="patient-selector-scheduled-result">
              <div class="flex w-14 shrink-0 label-large-prominent !tracking-[0.5px] text-primary mr-2 border-r border-primary-container">{{ formatTime(item) }}</div>
              <ng-container *ngTemplateOutlet="patientDetails; context: { $implicit: item }"></ng-container>
              <div class="shrink-0 flex items-center">
                <!-- Pointer events belong to the row, so the radio can never race it; keyboard still reports through (change). -->
                <mat-radio-button class="ml-2 pointer-events-none" name="patient-selector" [hidden]="!showRadios"
                  [checked]="isSelected(item)"
                  (change)="onSelectPatient(item, true, 'schedule')"></mat-radio-button>
              </div>
            </li>
          </ul>
        }
        @else if (isLoading) {
          <div class="h-full"></div>
        }
        @else {
          <div class="flex flex-col items-center justify-center h-full py-8">
            <img src="assets/folder_empty.svg" alt="" class="h-20 w-20 mb-4 opacity-70" />
            <div class="text-primary text-center">
              <p class="title-medium">{{ 'No patients found' | translate }}</p>
            </div>
          </div>
        }
      </div>
    </div>
  </mat-tab>
  <mat-tab [label]="'All patients' | translate">
    <div class="pt-4 pb-2 flex flex-col" [ngClass]="visitFlow ? 'px-0' : 'px-2'">
      <mat-form-field appearance="outline" class="w-full mb-3 [&_.mat-mdc-form-field-icon-suffix]:!translate-x-[-10px]" subscriptSizing="dynamic">
        <mat-label>{{ 'Search patient (by name or ID)' | translate }}</mat-label>
        <input matInput #searchInput (keydown.enter)="onSearch(searchInput.value)" />
        @if (visitFlow) {
          <button mat-icon-button matSuffix [attr.aria-label]="'Search' | translate"
            (click)="onSearch(searchInput.value)" data-testid="patient-selector-search-button">
            <mat-icon class="text-outline">search</mat-icon>
          </button>
        } @else {
          <button mat-icon-button matSuffix aria-label="Search" (click)="onSearch(searchInput.value)">
            <mat-icon>search</mat-icon>
          </button>
        }
      </mat-form-field>

      
      <!-- Taller than the scheduled list by the height of its filter chips and label, so the dialog keeps one height across tabs. -->
      <div class="relative overflow-y-auto grow" [ngClass]="visitFlow ? 'h-[370px]' : 'h-[292px]'">
        @if (showLoadingSkeleton) {
          <app-appointment-skeleton variant="selector" [count]="3"></app-appointment-skeleton>
        }
        @else if (allSubjects.value.length > 0) {
          <div class="label-medium text-outline mb-2">{{ 'Search results' | translate }}</div>
          <ul class="space-y-2 grow">
            <li *ngFor="let item of allSubjects.value"
              class="flex p-4 rounded-lg border border-surface-container-highest hover:bg-surface-container-high cursor-pointer"
              [ngClass]="rowTint(item)"
              (click)="onRowClick(item, 'search')"
              data-testid="patient-selector-search-result">
              <ng-container *ngTemplateOutlet="patientDetails; context: { $implicit: item }"></ng-container>
              <div class="shrink-0 flex items-center">
                <mat-radio-button class="ml-2 pointer-events-none" name="patient-selector" [hidden]="!showRadios"
                  [checked]="isSelected(item)"
                  (change)="onSelectPatient(item, true, 'search')"></mat-radio-button>
              </div>
            </li>
          </ul>
        }
        @else if (isLoading) {
          <div class="h-full"></div>
        }
        @else if (displaySearchIntructions) {
          <div class="flex flex-col items-center justify-center h-full py-8 gap-3">
            <mat-icon class="!text-6xl !w-14 !h-14 !text-primary">search</mat-icon>
            <div class="text-center">
              <p class="body-medium text-on-surface-variant w-80">{{ 'You must search for a patient above and press enter or the search button' | translate }}</p>
            </div>
          </div>
        }
        @else {
          <div class="flex flex-col items-center justify-center h-full py-8">
            <img src="assets/folder_empty.svg" alt="" class="h-20 w-20 mb-4 opacity-70" />
            <div class="text-primary text-center">
              <p class="title-medium">{{ 'No patients found' | translate }}</p>
            </div>
          </div>
        }
        
      </div>
    </div>
  </mat-tab>
</mat-tab-group>

<ng-template #patientDetails let-item>
  <div class="flex-1 min-w-0">
    <div class="label-large-prominent !tracking-[0.5px] text-primary truncate">{{ formatName(item) }}</div>
    <div class="label-medium-prominent text-on-surface-variant">{{ 'MRN' | translate }} {{ item.subject_id || '—' }}</div>
    <div class="mt-2 flex items-center gap-2 label-medium text-on-surface-variant">
      <ng-container *ngIf="!isLegalOrg">
        <span>{{ 'DOB:' | translate }} {{ formatDob(item) }}</span>
        <span class="w-[3px] h-[3px] rounded-full bg-outline-variant"></span>
      </ng-container>
      <span>{{ 'ALT-ID:' | translate }} {{ item.subject_alternative_id || '—' }}</span>
    </div>
  </div>
</ng-template>


